import {
  EFFECTS_DEFAULT,
  EFFECT_KEYS,
  SCENE_DEFAULT,
  UI_EFFECTS_STORAGE_KEY,
  effectsFromRestDoc,
  parseStoredEffects,
  type EffectKey,
  type EffectsConfig,
  UI_SCENE_STORAGE_KEY,
  applySceneUrlOverride,
  parseStoredScene,
  readUrlSceneMode,
  sceneFromRestDoc,
  type SceneConfig,
} from './uiThemeCore'
import { applyStoredAppIcon, syncAppIconFromDoc } from './appIcon'

/**
 * ฉากหลังองค์พ่อคู่พระแม่ — ตั้งแอตทริบิวต์บน <html> ให้ scene-v2.css ซ่อน/ปรับความเบลอ
 * ค่าที่ใช้: ?scene= > Firestore (แอดมินตั้ง · REST 1 ครั้ง/session) > localStorage > ค่าเริ่มต้น (เปิดทุกหน้า)
 * ใช้ค่าจาก localStorage ทันทีก่อนวาดหน้า — ไม่รอ Firestore (ลูกค้าครั้งแรกเห็นค่าเริ่มต้นแล้วค่อยปรับเมื่อ REST ตอบ)
 */
const SESSION_KEY = 'oc-scene-checked'
const UI_DOC_REST_URL =
  'https://firestore.googleapis.com/v1/projects/srikanett-order/databases/(default)/documents/publicIntegrations/ordercusUi'

let started = false

function setFlag(name: string, off: boolean): void {
  const root = document.documentElement
  if (off) root.setAttribute(name, 'off')
  else root.removeAttribute(name)
}

export function applyScene(config: SceneConfig): void {
  const root = document.documentElement
  setFlag('data-scene', !config.enabled)
  setFlag('data-scene-loading', !config.loading)
  setFlag('data-scene-member', !config.member)
  setFlag('data-scene-order', !config.order)
  if (config.blur === 'mid') root.removeAttribute('data-scene-blur')
  else root.setAttribute('data-scene-blur', config.blur)
  if (config.dim === 'mid') root.removeAttribute('data-scene-dim')
  else root.setAttribute('data-scene-dim', config.dim)
}

/** เอฟเฟกต์หน้าออร์เดอร์ — <html data-fx-*="off"> (ค่าจากแอดมิน · ดู uiThemeCore EffectsConfig) */
export function applyEffects(config: EffectsConfig): void {
  EFFECT_KEYS.forEach((k) => setFlag(`data-fx-${k}`, !config[k]))
  window.dispatchEvent(new CustomEvent('oc-fx-change'))
}

/** เอฟเฟกต์นี้เปิดอยู่ไหม (ตามค่าแอดมิน — ยังไม่รวมโหมดประหยัดพลังงาน) */
export function isEffectOn(key: EffectKey): boolean {
  return document.documentElement.getAttribute(`data-fx-${key}`) !== 'off'
}

/** เริ่มครั้งเดียวใน main.tsx (ก่อน createRoot) */
export function startSceneSync(): void {
  if (started) return
  started = true
  const urlMode = readUrlSceneMode(window.location.search)
  let stored: SceneConfig = { ...SCENE_DEFAULT }
  try {
    stored = parseStoredScene(localStorage.getItem(UI_SCENE_STORAGE_KEY))
  } catch {
    /* private mode */
  }
  applyScene(applySceneUrlOverride(stored, urlMode))
  let storedFx: EffectsConfig = { ...EFFECTS_DEFAULT }
  try {
    storedFx = parseStoredEffects(localStorage.getItem(UI_EFFECTS_STORAGE_KEY))
  } catch {
    /* private mode */
  }
  applyEffects(storedFx)
  applyStoredAppIcon() // ไอคอนแอปใช้ REST ครั้งเดียวกับฉากหลัง (ไม่เพิ่มการอ่าน Firestore)
  if (urlMode) return
  try {
    if (sessionStorage.getItem(SESSION_KEY)) return
  } catch {
    /* private mode — เช็คทุกครั้งที่เปิดหน้า */
  }
  const controller = typeof AbortController === 'undefined' ? null : new AbortController()
  const timer = window.setTimeout(() => controller?.abort(), 6000)
  fetch(UI_DOC_REST_URL, { signal: controller?.signal, cache: 'no-store' })
    .then((res) => (res.ok ? res.json() : Promise.reject(new Error(String(res.status)))))
    .then((doc) => {
      syncAppIconFromDoc(doc)
      const remote = sceneFromRestDoc(doc)
      const remoteFx = effectsFromRestDoc(doc)
      try {
        localStorage.setItem(UI_SCENE_STORAGE_KEY, JSON.stringify(remote))
        localStorage.setItem(UI_EFFECTS_STORAGE_KEY, JSON.stringify(remoteFx))
        sessionStorage.setItem(SESSION_KEY, '1')
      } catch {
        /* private mode / quota */
      }
      applyScene(remote)
      applyEffects(remoteFx)
    })
    .catch(() => {
      /* ออฟไลน์/ช้า — คงค่าเดิม */
    })
    .finally(() => window.clearTimeout(timer))
}
